// Cookie Consent Management Utility
// GDPR & CCPA Compliant

const CONSENT_KEY = 'sevenhc_cookie_consent';
const CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_DAYS = 365;

// Cookie Categories
export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
};

// Default consent state
const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  timestamp: null,
  preferences: {
    [COOKIE_CATEGORIES.NECESSARY]: true, // Always true, can't be disabled
    [COOKIE_CATEGORIES.FUNCTIONAL]: false,
    [COOKIE_CATEGORIES.ANALYTICS]: false,
    [COOKIE_CATEGORIES.MARKETING]: false,
  },
};

/**
 * Get current consent status
 */
export const getConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const consent = JSON.parse(stored);
    
    // Check if consent is expired
    const expiryDate = new Date(consent.timestamp);
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
    
    if (new Date() > expiryDate) {
      // Consent expired, clear it
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }

    return consent;
  } catch (error) {
    console.error('Error reading consent:', error);
    return null;
  }
};

/**
 * Save consent preferences
 */
export const saveConsent = (preferences) => {
  const previousConsent = getConsent();
  
  const consent = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    preferences: {
      [COOKIE_CATEGORIES.NECESSARY]: true, // Always enabled
      [COOKIE_CATEGORIES.FUNCTIONAL]: preferences.functional || false,
      [COOKIE_CATEGORIES.ANALYTICS]: preferences.analytics || false,
      [COOKIE_CATEGORIES.MARKETING]: preferences.marketing || false,
    },
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    
    // Check if consent was revoked for any category
    if (previousConsent) {
      // Analytics consent revoked
      if (previousConsent.preferences.analytics && !consent.preferences.analytics) {
        removeAnalyticsScripts();
      }
      
      // Marketing consent revoked (for future use)
      if (previousConsent.preferences.marketing && !consent.preferences.marketing) {
        // Remove marketing scripts when implemented
        console.log('Marketing consent revoked');
      }
    }
    
    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('cookieConsentChange', { detail: consent }));
    
    return consent;
  } catch (error) {
    console.error('Error saving consent:', error);
    return null;
  }
};

/**
 * Accept all cookies
 */
export const acceptAll = () => {
  return saveConsent({
    functional: true,
    analytics: true,
    marketing: true,
  });
};

/**
 * Reject all non-necessary cookies
 */
export const rejectAll = () => {
  return saveConsent({
    functional: false,
    analytics: false,
    marketing: false,
  });
};

/**
 * Check if a specific category is consented
 */
export const hasConsent = (category) => {
  const consent = getConsent();
  if (!consent) return false;
  return consent.preferences[category] === true;
};

/**
 * Clear all consent (for testing/reset)
 */
export const clearConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
  window.dispatchEvent(new CustomEvent('cookieConsentChange', { detail: null }));
};

/**
 * Check if consent banner should be shown
 */
export const shouldShowBanner = () => {
  return getConsent() === null;
};

// Google Analytics Integration
export const loadGoogleAnalytics = (measurementId) => {
  if (!hasConsent(COOKIE_CATEGORIES.ANALYTICS)) {
    console.log('Analytics consent not granted');
    return;
  }

  // Check if already loaded
  if (window.gtag) {
    console.log('Google Analytics already loaded, updating consent');
    updateGAConsent(true);
    return;
  }

  // Load GA4
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script1.setAttribute('data-cookie-consent', 'analytics');
  document.head.appendChild(script1);

  const script2 = document.createElement('script');
  script2.setAttribute('data-cookie-consent', 'analytics');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    
    // Configure with consent mode
    gtag('consent', 'default', {
      'analytics_storage': 'granted',
      'ad_storage': 'denied'
    });
    
    gtag('config', '${measurementId}', {
      'anonymize_ip': true,
      'cookie_flags': 'SameSite=None;Secure'
    });
  `;
  document.head.appendChild(script2);

  console.log('Google Analytics loaded');
};

// Update GA consent mode
export const updateGAConsent = (granted) => {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': granted ? 'granted' : 'denied'
    });
    console.log(`GA consent updated: ${granted ? 'granted' : 'denied'}`);
  }
};

// Remove analytics scripts and cookies
export const removeAnalyticsScripts = () => {
  // Remove GA scripts
  const scripts = document.querySelectorAll('script[data-cookie-consent="analytics"]');
  scripts.forEach(script => script.remove());
  
  // Clear GA cookies
  const gaCookies = ['_ga', '_gat', '_gid'];
  gaCookies.forEach(cookieName => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
  });
  
  // Clear cookies that start with _ga_
  document.cookie.split(';').forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    if (cookieName.startsWith('_ga_')) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    }
  });
  
  // Update GA consent mode if GA is loaded
  updateGAConsent(false);
  
  console.log('Analytics scripts and cookies removed');
};

// Listen for consent changes and load/unload scripts accordingly
window.addEventListener('cookieConsentChange', (event) => {
  const consent = event.detail;
  
  if (consent && consent.preferences[COOKIE_CATEGORIES.ANALYTICS]) {
    // Load analytics if consented
    // GA ID will be loaded from environment or config
    const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (GA_ID) {
      loadGoogleAnalytics(GA_ID);
    }
  }
});
